/**
 * Note Metadata Component
 *
 * Displays patient assignment and encounter information below the NoteTopBar.
 *
 * Features:
 * - 80px height with #FCFCFC background
 * - Patient assignment or patient name display with pronouns
 * - Encounter date, start time, and duration from encounter data (with fallback to transcription data)
 * - Editable encounter time when data comes from encounter (not transcription)
 * - Horizontal layout with dividers between items
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AssignNoteToPatient from '@components/AssignNoteToPatient';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { cleanCptCodes, CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker/ICD10CodePicker';
import { EligibilityBadge } from '@features/eligibility-checks';
import { useDebouncedWorkingBillingClaim } from '@features/encounters';
import {
  bareCodesToLineItems,
  buildDiagnosisCodeSources,
  extractBillingCodes,
  extractDiagnosisCodes,
  hasDiagnosisCodes,
  hasLineItems,
} from '@features/encounters/utils/billingClaim';
import { notesApi } from '@features/notes';
import { PatientContextIndicatorsGroup } from '@features/patient-context-bar';
import {
  getCoverageDisplayLabel,
  getCoverageDisplayStatus,
  getPatientSelfPayFlag,
  useGetPatientCoveragesQuery,
} from '@features/patient-coverages';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import { UserIcon } from '@jotpsych/ui/icons/UserIcon';
import {
  AttachMoney,
  AutoAwesome,
  CalendarToday,
  Check as CheckIcon,
  Close as CloseIcon,
  EditNote as EditNoteIcon,
  HealthAndSafety,
  LocalHospital,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Container,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import type { EncounterResponse } from '@store/generated/encounters';
import type { NoteResponse } from '@store/generated/notes';
import { useAppDispatch, useModal } from '@store/hooks';
import type { Patient } from '@ts/types';
import { parseUTCDate } from '@utils/dateUtils';
import { formatPatientName } from '@utils/patientUtils';
import { capitalize, formatDate } from '@utils/utils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { LAYOUT_SPACING } from '../constants/layout';
import { formatDocumentedBy } from '../hooks/useCosignContext';

export interface NoteMetadataProps {
  /** Note ID for patient assignment */
  noteId: string;
  /** Note data with patient and transcription information */
  noteData?: NoteResponse;
  /** Encounter data with start_time, end_time, and duration */
  encounterData?: EncounterResponse;
  /** Whether patient assignment/unassignment is allowed */
  editable?: boolean;
  /** AI-suggested CPT codes from audit analysis (shown as ghost when no codes exist) */
  suggestedCptCodes?: string[];
  /** Callback when user clicks to apply suggested CPT codes */
  onUseSuggestedCodes?: () => void;
  /** Callback when viewing last encounter note from patient context */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when CPT codes are changed (for triggering reaudit) */
  onCptCodesChanged?: (codes: string[]) => void;
  /** When provided, chip clicks open the unified billing popover instead of inline editors */
  onOpenBillingPopover?: (
    anchorEl: HTMLElement,
    section?: 'session' | 'billing' | 'attestations',
  ) => void;
  /** Callback when user clicks the payer chip to open patient insurance pane */
  onPayerClick?: () => void;
  /**
   * Suggested-codes expectation state (GDN-2614). Every field is optional and
   * defaults to "off": when the backend does not send `suggestion_status` the
   * bar renders exactly as it did before this feature existed.
   */
  suggestion?: {
    /** Codes are in flight — show the pending affordance. */
    isPending?: boolean;
    /** The suggestion deadline passed with nothing to show. */
    isTimedOut?: boolean;
    /** Codes just landed — play the arrival transition. */
    justArrived?: boolean;
  };
  /** Retry action for the timed-out state (triggers a reaudit). */
  onRetrySuggestion?: () => void;
  /** Whether a suggestion retry is currently running. */
  isRetryingSuggestion?: boolean;
}

type PatientDisplayExtras = {
  preferred_name?: string | null;
  pronouns?: string | null;
};

/** Label shown while the server says billing-code suggestions are in flight. */
export const SUGGESTION_PENDING_LABEL = 'Suggesting codes…';

/** Label shown when the suggestion deadline passes with nothing to show. */
export const SUGGESTION_TIMEOUT_LABEL = "Couldn't suggest codes";

/**
 * Shimmer used by the "suggesting codes…" pending state (GDN-2614 / U1).
 * A slow highlight sweep across the label — enough to read as "working",
 * quiet enough to sit in a metadata bar for a minute.
 */
const SUGGESTION_SHIMMER_KEYFRAMES = {
  '@keyframes noteMetadataSuggestionShimmer': {
    '0%': { backgroundPosition: '160% 0' },
    '100%': { backgroundPosition: '-60% 0' },
  },
};

/**
 * Arrival transition for late-landing codes. ~200ms fade + slide so the
 * provider notices the swap instead of it happening silently under the cursor.
 */
const SUGGESTION_ARRIVAL_KEYFRAMES = {
  '@keyframes noteMetadataSuggestionArrival': {
    from: { opacity: 0, transform: 'translateY(-4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

/** Subtle "AI" marker for codes the model proposed rather than the provider. */
const AiSourceBadge: React.FC = () => (
  <Chip
    label="AI"
    size="small"
    aria-label="AI-suggested"
    sx={{
      height: 16,
      fontSize: '0.625rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      bgcolor: 'action.hover',
      color: 'text.secondary',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
);

/**
 * Ghost chips for codes the model proposed but the provider hasn't accepted.
 *
 * Shared by CPT (suggestions from the audit revision) and ICD-10 (suggestions
 * persisted on the working claim as `ai_recommended`) so both code types get
 * the same dashed-chip + one-tap-accept treatment.
 */
const SuggestedCodesGhost: React.FC<{
  icon: React.ReactNode;
  codes: string[];
  tooltip: string;
  acceptTooltip: string;
  acceptAriaLabel: string;
  onOpenEditor: (event: React.MouseEvent<HTMLElement>) => void;
  onAccept?: () => void;
  arriving?: boolean;
}> = ({ icon, codes, tooltip, acceptTooltip, acceptAriaLabel, onOpenEditor, onAccept, arriving }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ml: 2,
        pl: 2,
        borderLeft: `1px solid ${theme.palette.divider}`,
        ...(arriving && {
          ...SUGGESTION_ARRIVAL_KEYFRAMES,
          animation: 'noteMetadataSuggestionArrival 200ms ease-out',
        }),
      }}
    >
      <Tooltip title={tooltip} arrow>
        <Box
          onClick={onOpenEditor}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            mx: -1,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          {icon}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {codes.map((code, idx) => (
              <Chip
                key={idx}
                label={code}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  opacity: 0.6,
                  borderStyle: 'dashed',
                  borderColor: theme.palette.text.disabled,
                  color: theme.palette.text.secondary,
                  pointerEvents: 'none', // Prevent chip clicks from interfering
                }}
              />
            ))}
          </Box>
        </Box>
      </Tooltip>
      {onAccept && (
        <Tooltip title={acceptTooltip} arrow>
          <IconButton
            size="small"
            aria-label={acceptAriaLabel}
            onClick={e => {
              e.stopPropagation(); // Prevent opening the picker
              onAccept();
            }}
            sx={{
              p: 0.5,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Metadata item display component
 * Shows icon + text with tooltip
 */
const MetadataItem: React.FC<{
  icon: React.ReactNode;
  text: string;
  tooltip: string;
  trailingIcon?: React.ReactNode;
  /** Free-form trailing node (e.g. a secondary chip) rendered after the text. */
  trailing?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  clickable?: boolean;
  /** Animate the label while a suggestion is in flight. */
  shimmer?: boolean;
  /** Play the ~200ms arrival transition (codes just landed). */
  arriving?: boolean;
}> = ({
  icon,
  text,
  tooltip,
  trailingIcon,
  trailing,
  onClick,
  clickable = false,
  shimmer = false,
  arriving = false,
}) => {
  const theme = useTheme();

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: theme.palette.text.secondary,
          ...(clickable && {
            cursor: 'pointer',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            mx: -1,
            my: -0.5,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }),
          ...(arriving && {
            ...SUGGESTION_ARRIVAL_KEYFRAMES,
            animation: 'noteMetadataSuggestionArrival 200ms ease-out',
          }),
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 20, height: 20 }}>
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            ...(shimmer && {
              ...SUGGESTION_SHIMMER_KEYFRAMES,
              backgroundImage: `linear-gradient(90deg, ${theme.palette.text.disabled} 0%, ${theme.palette.text.disabled} 35%, ${theme.palette.text.primary} 50%, ${theme.palette.text.disabled} 65%, ${theme.palette.text.disabled} 100%)`,
              backgroundSize: '220% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              animation: 'noteMetadataSuggestionShimmer 1.8s linear infinite',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                color: theme.palette.text.disabled,
              },
            }),
          }}
        >
          {text}
        </Typography>
        {trailingIcon && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 18, height: 18 }}>
            {trailingIcon}
          </Box>
        )}
        {trailing}
      </Box>
    </Tooltip>
  );
};

/**
 * Note Metadata Bar Component
 */
export const NoteMetadata: React.FC<NoteMetadataProps> = ({
  noteId,
  noteData,
  encounterData,
  editable = true,
  suggestedCptCodes,
  onUseSuggestedCodes,
  onViewLastEncounterNote,
  onCptCodesChanged,
  onOpenBillingPopover,
  onPayerClick,
  suggestion,
  onRetrySuggestion,
  isRetryingSuggestion = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const { enqueueSnackbar } = useSnackbar();
  const { note } = useEventTracking();
  const { open: openModal } = useModal();

  // State for hover and modal
  const [isHovering, setIsHovering] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  // State for CPT codes editing
  const [cptAnchorEl, setCptAnchorEl] = useState<HTMLElement | null>(null);

  // State for ICD-10 codes editing
  const [icd10AnchorEl, setIcd10AnchorEl] = useState<HTMLElement | null>(null);

  // Check if patient is assigned
  const patient = noteData?.patient;
  const patientDisplayExtras = patient as PatientDisplayExtras | undefined;
  const patientId = patient?.patient_id;
  const { data: coverageSummary } = useGetPatientCoveragesQuery(
    { patientId: patientId ?? '' },
    { skip: !patientId },
  );

  // Handler to open insurance modal
  const handleAddInsurance = useCallback(() => {
    if (patient?.patient_id) {
      openModal(MODAL_TYPES.INSURANCE_EDIT, {
        patientId: patient.patient_id,
      });
    }
  }, [patient?.patient_id, openModal]);
  const hasPatient = Boolean(patient);
  const hasPatientId = Boolean(noteData?.patient_id);
  const subjectLanguage = useSubjectLanguage();
  const encounterId = noteData?.encounter?.encounter_id ?? encounterData?.encounter_id;

  const { update: updateBillingClaim } = useDebouncedWorkingBillingClaim({
    encounterId,
    onSuccess: merged => {
      // Trigger reaudit after successful CPT code update
      if (merged.lineItems && onCptCodesChanged) {
        // extractBillingCodes only reads code_id; the WorkingClaimLineItem (wide
        // modifier_sources post-GDN-1733) vs WorkingBillingClaim.line_items (still
        // narrow) mismatch is benign here. Cast to bridge the structural gap.
        const codes = extractBillingCodes({
          line_items: merged.lineItems,
        });
        onCptCodesChanged(codes);
      }
    },
  });

  // Determine if time data comes from encounter (editable) or transcription (read-only)
  const hasEncounterTimeData = Boolean(
    encounterData?.start_time || encounterData?.appointment?.start_time,
  );

  /**
   * Handle opening time/session editor via unified billing popover
   */
  const handleOpenTimeEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterTimeEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
      });
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget, 'session');
        return;
      }
    },
    [noteId, encounterId, note, onOpenBillingPopover],
  );

  /**
   * Handle opening CPT editor popover
   */
  const handleOpenCptEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterCptEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: hasLineItems(encounterData?.working_billing_claim),
      });
      // Delegate to unified billing popover when available
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget);
        return;
      }
      setCptAnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.working_billing_claim, note, onOpenBillingPopover],
  );

  /**
   * Handle CPT codes change - auto-save immediately
   * CPTCodePicker now only fires onChange when codes are committed (not on typing)
   * Reaudit is triggered via onSuccess callback in useDebouncedWorkingBillingClaim
   */
  const handleBillingCodesChange = useCallback(
    (codes: string[]) => {
      const cleanedCodes = cleanCptCodes(codes);
      const claim = encounterData?.working_billing_claim;

      // Track event
      note.encounterCptCodesSaved({
        note_id: noteId,
        encounter_id: encounterId,
        cpt_codes: cleanedCodes,
        cpt_codes_count: cleanedCodes.length,
      });

      updateBillingClaim({
        lineItems: bareCodesToLineItems(cleanedCodes, claim?.line_items ?? undefined),
      });
    },
    [encounterData?.working_billing_claim, noteId, encounterId, note, updateBillingClaim],
  );

  /**
   * Handle closing CPT editor popover
   */
  const handleCloseCptEditor = useCallback(() => {
    setCptAnchorEl(null);
  }, []);

  const isCptEditorOpen = Boolean(cptAnchorEl);

  /**
   * Handle ICD-10 codes change - auto-save immediately
   * ICD10CodePicker only fires onChange when codes are added/removed (not on typing)
   */
  const handleDiagnosisCodesChange = useCallback(
    (codes: string[]) => {
      const filtered = codes.filter(c => c && c.trim() !== '');
      const claim = encounterData?.working_billing_claim;

      // Track event
      note.encounterIcd10CodesSaved?.({
        note_id: noteId,
        encounter_id: encounterId,
        icd10_codes: filtered,
        icd10_codes_count: filtered.length,
      });

      updateBillingClaim({
        diagnosisCodes: filtered,
        diagnosisCodeSources: buildDiagnosisCodeSources(filtered, claim?.diagnosis_code_sources),
      });
    },
    [encounterData?.working_billing_claim, noteId, encounterId, note, updateBillingClaim],
  );

  /**
   * Accept the model's diagnosis codes (GDN-2614 / U1).
   *
   * ICD-10 suggestions arrive already persisted on the working claim, tagged
   * `ai_recommended`. Accepting them doesn't change the codes — it re-stamps
   * their provenance as provider-selected, which is what makes the ghost
   * treatment resolve into a normal chip.
   */
  const handleUseSuggestedDiagnosisCodes = useCallback(
    (codes: string[]) => {
      if (codes.length === 0) return;

      note.encounterIcd10CodesSaved?.({
        note_id: noteId,
        encounter_id: encounterId,
        icd10_codes: codes,
        icd10_codes_count: codes.length,
      });

      // `accepted_suggestion`, not a provider edit: agreeing with the model must
      // not tear down the CPT pending/arrival/timeout UI, whose recommendation
      // comes from the audit pipeline on independent timing and may still be in
      // flight (GDN-2618). The write is still recorded, so a pushed claim can't
      // clobber what was just accepted.
      updateBillingClaim(
        {
          diagnosisCodes: codes,
          diagnosisCodeSources: Object.fromEntries(
            codes.map(code => [code, 'provider_selected' as const]),
          ),
        },
        { origin: 'accepted_suggestion' },
      );
    },
    [noteId, encounterId, note, updateBillingClaim],
  );

  /**
   * Handle opening ICD-10 editor popover
   */
  const handleOpenIcd10Editor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterIcd10EditorOpened?.({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: hasDiagnosisCodes(encounterData?.working_billing_claim),
      });
      // Delegate to unified billing popover when available
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget);
        return;
      }
      setIcd10AnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.working_billing_claim, note, onOpenBillingPopover],
  );

  /**
   * Handle closing ICD-10 editor popover
   */
  const handleCloseIcd10Editor = useCallback(() => {
    setIcd10AnchorEl(null);
  }, []);

  const isIcd10EditorOpen = Boolean(icd10AnchorEl);

  /**
   * Handle patient assignment with optimistic update
   * Updates RTK Query cache for immediate UI feedback
   */
  const handleAssignPatient = useCallback(
    (assignedPatient: Patient) => {
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
          draft.patient = assignedPatient;
          draft.patient_id = assignedPatient.patient_id;
        }),
      );
    },
    [dispatch, noteId],
  );

  /**
   * Handle patient assignment error
   * Invalidates cache to refetch fresh data
   */
  const handleAssignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment error
   * Invalidates cache to refetch fresh data
   */
  const handleUnassignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment with optimistic update
   * Removes patient from RTK Query cache and calls API
   */
  const handleUnassign = useCallback(async () => {
    // Close modal immediately
    setShowUnassignModal(false);

    // Optimistically update cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        draft.patient = null;
        draft.patient_id = null;
      }),
    );

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: `/patient/unassignpatient`,
        accessToken,
        body: {
          note_id: noteId,
        },
      });

      if (response.ok) {
        enqueueSnackbar('Patient unassigned successfully', {
          variant: 'success',
        });
        // Track successful unassignment
        note.patientUnassigned(noteId, {
          patient_id: patient?.patient_id,
          encounter_id: encounterId,
        });
      } else {
        throw new Error('Failed to unassign patient');
      }
    } catch (error) {
      console.error('Error unassigning patient:', error);
      enqueueSnackbar('Failed to unassign patient', { variant: 'error' });

      // On error, invalidate cache to refetch correct data
      handleUnassignError();
    }
  }, [dispatch, noteId, getAccessToken, enqueueSnackbar, handleUnassignError]);

  /**
   * Navigate to patient page
   */
  const handlePatientClick = useCallback(() => {
    if (patient?.patient_id) {
      note.patientNameClicked(noteId, {
        patient_id: patient.patient_id,
        encounter_id: encounterId,
      });
      navigate(`/patient/${patient.patient_id}`);
    }
  }, [navigate, patient, noteId, encounterId, note]);

  /**
   * Handle unassign icon click
   */
  const handleUnassignClick = useCallback(() => {
    note.patientUnassignClicked(noteId, {
      patient_id: patient?.patient_id,
      encounter_id: encounterId,
    });
    setShowUnassignModal(true);
  }, [patient, noteId, encounterId, note]);

  // Determine if encounter time is editable (data comes from encounter, not transcription)
  const isTimeEditable = editable && hasEncounterTimeData && Boolean(encounterId);

  // Determine if CPT codes are editable (only if we have encounter data)
  const isCptEditable = editable && Boolean(encounterId && encounterData);
  const hasCptCodes = hasLineItems(encounterData?.working_billing_claim);
  // GDN-3911: "Documented by {drafter} · {role}", or null when the note was
  // documented by its own author (the overwhelmingly normal case).
  const documentedByLabel = formatDocumentedBy(noteData?.documented_by);

  // Build metadata items array
  const metadataItems: Array<{
    icon: React.ReactNode;
    text: string;
    tooltip: string;
    trailingIcon?: React.ReactNode;
    trailing?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    clickable?: boolean;
    shimmer?: boolean;
    arriving?: boolean;
  }> = [];

  // Determine start time with fallback chain:
  // 1. encounterData.start_time (from separate useGetEncounterQuery hook)
  // 2. encounterData.appointment.start_time (if encounter doesn't have start_time directly)
  // 3. noteData.transcription_start_timestamp (fallback if encounter data unavailable)
  let startTimeEpoch: number | null = null;
  if (encounterData?.start_time) {
    // Encounter start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
  } else if (encounterData?.appointment?.start_time) {
    // Appointment start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
  } else if (noteData?.transcription_start_timestamp) {
    // Transcription timestamp is already in epoch seconds
    startTimeEpoch = noteData.transcription_start_timestamp;
  }

  // Calculate duration with fallback chain:
  // 1. Calculate from encounter start_time and end_time
  // 2. Use appointment duration (if encounter times not available)
  // 3. Fall back to transcription_length_seconds
  let durationSeconds: number | null = null;
  if (encounterData?.start_time && encounterData?.end_time) {
    // Calculate from encounter start/end times
    const start = new Date(encounterData.start_time).getTime();
    const end = new Date(encounterData.end_time).getTime();
    durationSeconds = Math.floor((end - start) / 1000);
  } else if (encounterData?.appointment?.duration) {
    // Appointment duration is in minutes, convert to seconds
    durationSeconds = encounterData.appointment.duration * 60;
  } else if (noteData?.transcription_length_seconds) {
    // Fall back to transcription length
    durationSeconds = noteData.transcription_length_seconds;
  }

  // GDN-2017: the displayed encounter date is the canonical service date, NOT
  // the recording clock. For a dictation the start_time is the dictation moment
  // (a different day than the visit), so deriving the date from start_time shows
  // the wrong date. The start_time/end_time clock still supplies the time-of-day
  // and duration, but only when it actually falls on the service date — otherwise
  // it would imply the visit happened at the recording time.
  // service_date is the backend-resolved canonical service day (GDN-2017 PR-0:
  // date_of_service -> service_rendered_at -> start_time -> scheduled_for ->
  // created_at). It is the right date for the chip; the recording clock
  // (start_time) is only a legacy fallback for the time-of-day read-out.
  const serviceDateStr = encounterData?.service_date ?? null;
  // Parse the date-only string in local time to avoid a UTC day-boundary shift.
  const serviceDate = serviceDateStr ? new Date(`${serviceDateStr}T00:00:00`) : null;
  const dateEpoch = serviceDate != null ? serviceDate.getTime() / 1000 : startTimeEpoch;

  // GDN-2510: when the recording day diverges from the displayed service date
  // (a back-dated dictation), append a small muted "dictated {Mon D}" chip.
  // The recording day is the transcription clock; the encounter clock only
  // counts when it still IS the recording clock (time_source null or in_room —
  // a scheduled/manual choice overwrites start_time with non-recording times).
  let recordingDate: Date | null = null;
  if (noteData?.transcription_start_timestamp) {
    recordingDate = new Date(noteData.transcription_start_timestamp * 1000);
  } else if (
    encounterData?.start_time &&
    (encounterData.time_source == null || encounterData.time_source === 'in_room')
  ) {
    // Server-origin timestamps may be naive (no offset) but denote UTC;
    // a bare new Date() would read them as local and can shift the computed
    // day near a UTC midnight boundary — the exact error this badge exists
    // to flag. Same parsing rule as officialTimeModel/recordingClassification.
    const d = parseUTCDate(encounterData.start_time);
    if (d && !isNaN(d.getTime())) recordingDate = d;
  }
  const displayedDate = dateEpoch ? new Date(dateEpoch * 1000) : null;
  const dictatedOn =
    recordingDate && displayedDate && recordingDate.toDateString() !== displayedDate.toDateString()
      ? format(recordingDate, 'MMM d')
      : null;

  // Add encounter date/time as a single clickable item if available
  if (dateEpoch) {
    // Use short month format (e.g., "Dec 11, 2025" instead of "December 11, 2025")
    const dateText = format(new Date(dateEpoch * 1000), 'MMM d, yyyy');

    const clockMatchesServiceDate =
      startTimeEpoch != null &&
      (serviceDate == null ||
        new Date(startTimeEpoch * 1000).toDateString() === serviceDate.toDateString());

    let timeText = '';
    if (clockMatchesServiceDate && startTimeEpoch != null) {
      const startTimeFormatted = formatDate(startTimeEpoch, 'time').toLowerCase();
      if (durationSeconds) {
        // Format as time range with duration (e.g., "11:30 am – 12:15 pm, 45 min")
        const endTimeFormatted = formatDate(
          startTimeEpoch + durationSeconds,
          'time',
        ).toLowerCase();

        // Format duration concisely
        const hours = Math.floor(durationSeconds / 3600);
        const minutes = Math.floor((durationSeconds % 3600) / 60);

        let durationText = '';
        if (hours > 0) {
          durationText = `${hours} hr${hours > 1 ? 's' : ''}`;
          if (minutes > 0) {
            durationText += ` ${minutes} min`;
          }
        } else if (minutes > 0) {
          durationText = `${minutes} min`;
        } else {
          durationText = `${Math.floor(durationSeconds)} sec`;
        }

        timeText = `${startTimeFormatted} – ${endTimeFormatted}, ${durationText}`;
      } else {
        timeText = startTimeFormatted;
      }
    }

    metadataItems.push({
      icon: <CalendarToday />,
      text: timeText ? `${dateText} • ${timeText}` : dateText,
      tooltip: isTimeEditable ? 'Click to edit encounter date and time' : 'Encounter Date and Time',
      // Muted "dictated {Mon D}" chip — only when the recording day diverges
      // from the displayed service date (GDN-2510).
      trailing: dictatedOn ? (
        <Tooltip title={`This note was dictated on ${dictatedOn}, after the visit`} arrow>
          <Chip
            label={`dictated ${dictatedOn}`}
            size="small"
            sx={{
              ml: 0.5,
              height: 20,
              fontSize: '0.6875rem',
              color: 'text.secondary',
            }}
          />
        </Tooltip>
      ) : undefined,
      onClick: isTimeEditable ? handleOpenTimeEditor : undefined,
      clickable: isTimeEditable,
    });
  }

  // GDN-3911: attribution for a note someone drafted on the author's behalf.
  // The author of record (who signs) is already shown in the signature block;
  // this names the person who actually did the documenting, which is otherwise
  // invisible once the note is signed by someone else.
  if (documentedByLabel) {
    metadataItems.push({
      icon: <EditNoteIcon />,
      text: documentedByLabel,
      tooltip: 'This note was documented on the signing provider’s behalf',
    });
  }

  // Add payer/insurance chip between date and CPT. Pick the coverage in
  // force today (GDN-2411) — a plan past its termination date must not
  // show as the patient's payer just because it sits first in the array.
  const insuranceCoverages = coverageSummary?.coverages?.length
    ? coverageSummary.coverages
    : patient?.insurance_coverages;
  const primaryCoverage =
    Array.isArray(insuranceCoverages) && insuranceCoverages.length > 0
      ? (insuranceCoverages.find(c => {
          const displayStatus = getCoverageDisplayStatus(c);
          return displayStatus === 'active' || displayStatus === 'pending';
        }) ?? null)
      : null;
  // A designated self-pay patient has no coverage rows, so the chip's
  // label falls back to the flag. Prefer the coverage-list response's
  // flag: it is invalidation-connected (a designation write refreshes
  // it), while the note's embedded patient is a prop that can go stale
  // for the life of the open note.
  const patientSelfPayFlag = Boolean(
    getPatientSelfPayFlag(coverageSummary) ?? getPatientSelfPayFlag(patient),
  );
  const primaryCoverageDisplay =
    getCoverageDisplayLabel(primaryCoverage) ?? (patientSelfPayFlag ? 'Self-pay' : null);
  // Flag-only since the GDN-3177 contract phase: designation never comes
  // from a coverage row's kind. (getCoverageDisplayLabel above may still
  // LABEL a lingering self-pay row "Self-pay" — that describes the row,
  // not the patient.)
  const primaryCoverageIsSelfPay = patientSelfPayFlag;
  const primaryCoveragePayerLogoUrl =
    primaryCoverage && 'payer_logo_url' in primaryCoverage ? primaryCoverage.payer_logo_url : null;
  if (primaryCoverageDisplay) {
    const payerIcon =
      !primaryCoverageIsSelfPay && primaryCoveragePayerLogoUrl ? (
        <Box
          component="img"
          src={primaryCoveragePayerLogoUrl}
          alt={primaryCoverageDisplay}
          sx={{
            width: 20,
            height: 20,
            maxWidth: 20,
            maxHeight: 20,
            objectFit: 'contain',
            borderRadius: 0.5,
          }}
        />
      ) : (
        <HealthAndSafety />
      );

    metadataItems.push({
      icon: payerIcon,
      text: primaryCoverageDisplay,
      tooltip: onPayerClick
        ? 'Click to view patient coverage'
        : `Coverage: ${primaryCoverageDisplay}`,
      onClick: onPayerClick
        ? () => {
            note.payerChipClicked(noteId, {
              encounter_id: encounterId,
              patient_id: patient?.patient_id,
              has_insurance: !primaryCoverageIsSelfPay,
              payer_name: primaryCoverageDisplay,
            });
            onPayerClick();
          }
        : undefined,
      clickable: Boolean(onPayerClick),
    });
  } else if (patient?.patient_id && onPayerClick) {
    metadataItems.push({
      icon: <HealthAndSafety />,
      text: 'Insurance',
      tooltip: 'Click to add patient insurance',
      onClick: () => {
        note.payerChipClicked(noteId, {
          encounter_id: encounterId,
          patient_id: patient?.patient_id,
          has_insurance: false,
        });
        onPayerClick();
      },
      clickable: true,
    });
  }

  // Check if we have suggested codes to show as ghost
  const hasSuggestedCodes = Boolean(
    suggestedCptCodes &&
    suggestedCptCodes.length > 0 &&
    suggestedCptCodes.some(c => c && c.trim() !== ''),
  );
  const filteredSuggestedCodes = suggestedCptCodes?.filter(c => c && c.trim() !== '') || [];

  // ---------------------------------------------------------------------------
  // Suggested-codes states (GDN-2614 / U1 + U2)
  //
  // All three flags default to false, so a backend that doesn't send
  // `suggestion_status` produces exactly the pre-GDN-2614 rendering below.
  // ---------------------------------------------------------------------------
  const isSuggestionPending = Boolean(suggestion?.isPending);
  const isSuggestionTimedOut = Boolean(suggestion?.isTimedOut);
  const didSuggestionJustArrive = Boolean(suggestion?.justArrived);

  const workingClaim = encounterData?.working_billing_claim;
  const cptLineItems = workingClaim?.line_items ?? [];
  const hasAiSourcedCptCodes = cptLineItems.some(item => item.source === 'ai_recommended');

  // Add CPT codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (hasCptCodes || (isCptEditable && !hasSuggestedCodes)) {
    if (hasCptCodes) {
      // Show existing CPT codes (clickable only when editable)
      const cptText = extractBillingCodes(workingClaim).join(', ');
      metadataItems.push({
        icon: <AttachMoney />,
        text: cptText,
        tooltip: isCptEditable ? 'Click to edit CPT codes' : 'CPT codes',
        onClick: isCptEditable ? handleOpenCptEditor : undefined,
        clickable: isCptEditable,
        trailing: hasAiSourcedCptCodes ? <AiSourceBadge /> : undefined,
        arriving: didSuggestionJustArrive,
      });
    } else if (isCptEditable && !hasSuggestedCodes) {
      // No codes yet. If the server told us codes are on the way, say so
      // instead of showing a bare prompt that reads "nothing is coming" —
      // the chip stays clickable so manual entry is never blocked.
      metadataItems.push({
        icon: <AttachMoney />,
        text: isSuggestionPending ? SUGGESTION_PENDING_LABEL : 'CPT',
        tooltip: isSuggestionPending
          ? 'Suggesting CPT codes — click to enter them yourself'
          : 'Add CPT codes',
        onClick: handleOpenCptEditor,
        clickable: true,
        shimmer: isSuggestionPending,
      });
    }
    // Note: When hasSuggestedCodes is true and !hasCptCodes, we render ghost codes separately below
  }

  // Determine if ICD-10 codes are editable (only if we have encounter data)
  const isIcd10Editable = editable && Boolean(encounterId && encounterData);
  const hasIcd10Codes = hasDiagnosisCodes(workingClaim);
  const icd10Codes = extractDiagnosisCodes(workingClaim);
  const diagnosisCodeSources = workingClaim?.diagnosis_code_sources;
  const aiSuggestedIcd10Codes = icd10Codes.filter(
    code => diagnosisCodeSources?.[code] === 'ai_recommended',
  );
  const hasAiSourcedIcd10Codes = aiSuggestedIcd10Codes.length > 0;

  // ICD-10 has no separate suggestion endpoint the way CPT does — the model's
  // proposals land straight in the persisted claim, tagged `ai_recommended`.
  // When *every* diagnosis code is still model-proposed, treat them the same
  // way the CPT ghost treats an unaccepted suggestion: dashed ghost chips plus
  // a one-tap "use suggested" that re-stamps them as provider-selected.
  const showIcd10Ghost =
    isIcd10Editable && icd10Codes.length > 0 && aiSuggestedIcd10Codes.length === icd10Codes.length;

  // Add ICD-10 codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (!showIcd10Ghost && (hasIcd10Codes || isIcd10Editable)) {
    if (hasIcd10Codes) {
      // Show primary code + count of additional codes
      const icd10Text =
        icd10Codes.length > 1 ? `${icd10Codes[0]} +${icd10Codes.length - 1}` : icd10Codes[0];
      metadataItems.push({
        icon: <LocalHospital />,
        text: icd10Text,
        tooltip: isIcd10Editable
          ? `Click to edit ICD-10 codes: ${icd10Codes.join(', ')}`
          : `ICD-10: ${icd10Codes.join(', ')}`,
        onClick: isIcd10Editable ? handleOpenIcd10Editor : undefined,
        clickable: isIcd10Editable,
        trailing: hasAiSourcedIcd10Codes ? <AiSourceBadge /> : undefined,
        arriving: didSuggestionJustArrive,
      });
    } else if (isIcd10Editable) {
      // No codes yet — mirror the CPT pending/prompt split.
      metadataItems.push({
        icon: <LocalHospital />,
        text: isSuggestionPending ? SUGGESTION_PENDING_LABEL : 'ICD-10',
        tooltip: isSuggestionPending
          ? 'Suggesting diagnosis codes — click to enter them yourself'
          : 'Add diagnosis codes',
        onClick: handleOpenIcd10Editor,
        clickable: true,
        shimmer: isSuggestionPending,
      });
    }
  }

  // Timeout honesty (U2): the deadline passed and nothing landed. Say so once,
  // pipeline-wide, with a retry — and leave the manual prompts above intact.
  // Deliberately NOT tied to quota: over-quota users still get codes; quota only
  // gates the compliance sidebar.
  if (isSuggestionTimedOut) {
    metadataItems.push({
      icon: <AutoAwesome sx={{ fontSize: 18 }} />,
      text: SUGGESTION_TIMEOUT_LABEL,
      tooltip: onRetrySuggestion
        ? 'We could not suggest billing codes for this note. Try again, or enter codes yourself.'
        : 'We could not suggest billing codes for this note. Enter codes yourself.',
      trailing: onRetrySuggestion ? (
        <Tooltip title="Try suggesting codes again" arrow>
          <span>
            <IconButton
              size="small"
              aria-label="Retry code suggestions"
              disabled={isRetryingSuggestion}
              onClick={event => {
                event.stopPropagation();
                onRetrySuggestion();
              }}
              sx={{ p: 0.25, ml: 0.5 }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : undefined,
    });
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCFCFC',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container
        sx={{
          px: LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          py: 2,
          columnGap: 3,
          rowGap: 0.5,
        }}
        maxWidth={'xxl'}
      >
        {/* Patient Assignment or Display */}
        {!hasPatient && !hasPatientId ? (
          editable && (
            <AssignNoteToPatient
              noteId={noteId}
              encounterId={encounterId}
              onAssign={handleAssignPatient}
              onAssignError={handleAssignError}
            />
          )
        ) : !hasPatient && hasPatientId ? (
          <Tooltip
            title={`A ${subjectLanguage} is assigned to this note, but you don't have permission to view their information. Ask your practice administrator if you think you should have access.`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' }}>
              <UserIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  color: theme.palette.text.disabled,
                  fontStyle: 'italic',
                }}
              >
                {capitalize(subjectLanguage)} assigned
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
              maxWidth: '100%',
              gap: 1,
            }}
            onMouseEnter={() => editable && setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Patient name, pronouns, eligibility icon, and context indicators - all on same line */}
            {editable && (
              <IconButton size="small" onClick={handleUnassignClick} sx={{ p: 0.5 }}>
                {isHovering ? (
                  <CloseIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : (
                  <UserIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                )}
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', gap: 0.5 }}>
              <Typography
                variant="body2"
                onClick={handlePatientClick}
                sx={{
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                  textDecoration: isHovering ? 'underline' : 'none',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                }}
              >
                {formatPatientName(
                  patient?.first_name,
                  patient?.last_name,
                  patientDisplayExtras?.preferred_name,
                )}
                {/* Conditionally display pronouns if available */}
                {patientDisplayExtras?.pronouns && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    ({patientDisplayExtras.pronouns})
                  </Typography>
                )}
              </Typography>
              <EligibilityBadge
                patientId={patient?.patient_id}
                encounterId={encounterId}
                patientName={
                  patient
                    ? formatPatientName(
                        patient.first_name ?? '',
                        patient.last_name ?? '',
                        patient.preferred_name,
                      )
                    : undefined
                }
                source="note_metadata"
                showActionLabel={false}
              />
            </Box>
            {/* Patient context indicators on same line */}
            {patient?.patient_id && (
              <PatientContextIndicatorsGroup
                patientId={patient.patient_id}
                encounterId={encounterId}
                source="note"
                onViewLastEncounterNote={onViewLastEncounterNote}
              />
            )}
          </Box>
        )}

        {/* Metadata Items */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1,
            '& > *:not(:last-child)::after': {
              content: '""',
              display: 'inline-block',
              width: '1px',
              height: 24,
              bgcolor: theme.palette.divider,
              ml: 2,
            },
          }}
        >
          {metadataItems.map((item, index) => (
            <MetadataItem
              key={index}
              icon={item.icon}
              text={item.text}
              tooltip={item.tooltip}
              trailingIcon={item.trailingIcon}
              trailing={item.trailing}
              onClick={item.onClick}
              clickable={item.clickable}
              shimmer={item.shimmer}
              arriving={item.arriving}
            />
          ))}
        </Box>

        {/* Ghost Suggested CPT Codes - shown when no codes exist but suggestions are available */}
        {isCptEditable && !hasCptCodes && hasSuggestedCodes && (
          <SuggestedCodesGhost
            icon={
              <AttachMoney
                fontSize="small"
                sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}
              />
            }
            codes={filteredSuggestedCodes}
            tooltip="Click to edit CPT codes. JotPsych suggests these codes based on your note content"
            acceptTooltip="Accept suggested codes"
            acceptAriaLabel="Use suggested CPT codes"
            onOpenEditor={handleOpenCptEditor}
            onAccept={onUseSuggestedCodes}
            arriving={didSuggestionJustArrive}
          />
        )}

        {/* Ghost Suggested ICD-10 Codes - every diagnosis code is still AI-proposed */}
        {showIcd10Ghost && (
          <SuggestedCodesGhost
            icon={
              <LocalHospital
                fontSize="small"
                sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}
              />
            }
            codes={aiSuggestedIcd10Codes}
            tooltip="Click to edit diagnosis codes. JotPsych suggests these codes based on your note content"
            acceptTooltip="Accept suggested diagnosis codes"
            acceptAriaLabel="Use suggested ICD-10 codes"
            onOpenEditor={handleOpenIcd10Editor}
            onAccept={() => handleUseSuggestedDiagnosisCodes(aiSuggestedIcd10Codes)}
            arriving={didSuggestionJustArrive}
          />
        )}

        {/* CPT Codes Editor Popover */}
        <Popover
          open={isCptEditorOpen}
          anchorEl={cptAnchorEl}
          onClose={handleCloseCptEditor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">CPT Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to two CPT codes for this encounter (optional).
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseCptEditor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <CPTCodePicker
                value={extractBillingCodes(encounterData?.working_billing_claim)}
                lineItems={encounterData?.working_billing_claim?.line_items ?? undefined}
                onChange={handleBillingCodesChange}
                showHeader={false}
                suggestedCodes={suggestedCptCodes}
                onUseSuggestedCodes={
                  onUseSuggestedCodes
                    ? () => {
                        onUseSuggestedCodes();
                        // Close the popover after applying suggested codes
                        setCptAnchorEl(null);
                      }
                    : undefined
                }
              />
            </Stack>
          </Box>
        </Popover>

        {/* ICD-10 Codes Editor Popover */}
        <Popover
          open={isIcd10EditorOpen}
          anchorEl={icd10AnchorEl}
          onClose={handleCloseIcd10Editor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">ICD-10 Diagnosis Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to 4 diagnosis codes. The first code is the primary diagnosis.
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseIcd10Editor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <ICD10CodePicker
                value={extractDiagnosisCodes(encounterData?.working_billing_claim)}
                onChange={handleDiagnosisCodesChange}
                showHeader={false}
              />
            </Stack>
          </Box>
        </Popover>

        {/* Unassignment Confirmation Modal */}
        <ConfirmDialog
          open={showUnassignModal}
          variant="warning"
          title="Unassign Patient"
          message={`Are you sure you want to unassign ${formatPatientName(
            patient?.first_name,
            patient?.last_name,
            patientDisplayExtras?.preferred_name,
          )} from this note?`}
          onClose={() => setShowUnassignModal(false)}
          onConfirm={handleUnassign}
          confirmText="Unassign"
        />
      </Container>
    </Box>
  );
};
